import type { Paragraph } from '@/api/types/stowed-element'
import { buildLinkedFrameStyleSeed } from '@/lib/text-linking/build-linked-text-frame'
import { paragraphsHaveText } from '@/lib/text-linking/merge-overflow-paragraphs'
import type { TextStory } from './types/text-story'
import type { StorySlice } from './types/story-slice'
import type { ChainSeams } from './types/chain-seams'
import type { DistributeStoryResult } from './types/distribute-story-result'
import type { DistributeStoryOptions } from './types/distribute-story-options'
import type { StoryFrame } from './types/story-frame'
import { fuseLists, paragraphHasText, seamWasCharacterSplit } from './helpers/seam-probe'

/**
 * Assembles a chain's frames into one story, re-joining paragraphs a previous split cut in half.
 *
 * This is the inverse of `distributeStory`, and the pair is lossless: split then rejoin returns the
 * original story.
 *
 * Why it cannot be a plain concatenation: `splitShapeTextParagraphsAtOffset` writes a mid-paragraph
 * split as `{...paragraph, textLines: keptLines}` and `{...paragraph, textLines: overflowLines}` —
 * two `Paragraph` objects with byte-identical metadata. Concatenating them puts a hard paragraph
 * break mid-sentence, because `paragraphsToPlainText` joins paragraphs with `\n`.
 *
 * `seams` supplies that answer directly for boundaries this session created. It is consulted only
 * where it can still be trusted; everywhere else the seam is probed. Two guards, because a memo can
 * outlive the distribution that produced it:
 *
 * 1. Only when the frame it describes is the *immediately preceding frame carrying text*. A textless
 *    frame between the two shifts the correspondence, so probe instead.
 * 2. **Never fuse onto an empty leading paragraph.** `distributeStory` never produces a slice whose
 *    first paragraph is blank, so a blank one can only be a break the user typed — and fusing it
 *    destroys that Enter with nothing to re-split it. (The old chain engine's `mergeOverflowIntoExisting`
 *    fuses it unconditionally today, so this guard is a fix, not a new restriction.)
 */
export function storyFromChain(frames: readonly StoryFrame[], seams?: ChainSeams): TextStory {
  const first = frames[0]

  if (!first) return []

  let story: Paragraph[] = first.paragraphs
  // The last frame whose content actually joined the story, and its index — the probe re-fits into
  // ITS box, and the memo is only trustworthy when it names the immediately preceding frame.
  let upstream: StoryFrame | undefined = paragraphsHaveText(first.paragraphs) ? first : undefined
  let upstreamIndex = 0

  for (let i = 1; i < frames.length; i++) {
    const right = frames[i]!

    // A frame holding only a style-seed placeholder has no content of its own to contribute, and
    // must not be allowed to stand as a seam boundary either.
    if (!paragraphsHaveText(right.paragraphs)) continue

    if (!upstream || !paragraphsHaveText(story)) {
      story = right.paragraphs
      upstream = right
      upstreamIndex = i
      continue
    }

    const memo = upstreamIndex === i - 1 ? seams?.[upstreamIndex] : undefined
    const fuse =
      paragraphHasText(right.paragraphs[0]) &&
      // Probed against the ORIGINAL pair either side of this boundary, never the accumulation: the
      // probe re-fits into ONE frame's box, so handing it several frames' worth of text would
      // overflow it wildly and reject every seam.
      (memo ?? seamWasCharacterSplit(upstream.paragraphs, right.paragraphs, upstream.fit))

    story = fuse ? fuseLists(story, right.paragraphs) : [...story, ...right.paragraphs]
    upstream = right
    upstreamIndex = i
  }

  return story
}

/**
 * A frame's slice, or — when the distribution left it with no text — a style-only blank paragraph
 * carrying its OWN previous typography.
 *
 * Never `[]`. Reporting an empty array discards the frame's font, so text typed into it afterwards
 * falls through to the hardcoded system default (AWPCHelvetica); worse, for a shape-backed member
 * `paragraphsForWriteBack` (`pages/home/helpers/`) then substitutes `defaultShapeTextParagraphs()` and the
 * element reloads as a plain shape, dropping out of its chain entirely (WMPCM-8160).
 */
function sliceOrSeed(frame: StoryFrame, next: Paragraph[]): Paragraph[] {
  if (paragraphsHaveText(next)) return next

  return buildLinkedFrameStyleSeed(frame.paragraphs)
}

/**
 * Splits `story` across `frames` in chain order, filling each to capacity before moving on.
 *
 * This is the whole flow engine. There is no merge step, no carryover fusing, no backward pull and
 * no options describing what kind of edit provoked it — a chain that has spare room upstream pulls
 * text up because re-splitting the story from the head is all this does. "Text flows back when frame
 * 1 grows" is not a feature here; it is the absence of one.
 *
 * `fillToCharacter` is derived from "has a downstream frame" by the early-out below rather than from
 * any field, so it can never disagree with the hand-off it pairs with — and the last frame's fitter
 * is not called at all, which satisfies the "never fill to character on a solo or last frame" rule
 * more strongly than passing `false` would. `verifyKeptFits` is set because every `kept` here becomes
 * a frame's painted content: a character this fitter keeps but the renderer cannot draw would vanish
 * at the seam with no badge to report it (WMPCM-9070, 2026-08-24 Bug 1).
 */
export function distributeStory(
  story: TextStory,
  frames: readonly StoryFrame[],
  options?: DistributeStoryOptions
): DistributeStoryResult {
  const slices: StorySlice[] = []
  const seams: boolean[] = []
  let remaining: Paragraph[] = story
  // Assigned wherever `remaining` is, so the two can never describe different splits.
  let remainderIsFragment = false

  frames.forEach((frame, index) => {
    const isLast = index === frames.length - 1

    if (isLast && options?.tailOverflowsPastLastFrame !== true) {
      slices.push({ id: frame.id, paragraphs: sliceOrSeed(frame, remaining) })
      remaining = []
      remainderIsFragment = false

      return
    }

    const split = frame.fit(remaining, { fillToCharacter: true, verifyKeptFits: true })

    if (!split) {
      // Everything left fits here; every frame after this one is drained.
      slices.push({ id: frame.id, paragraphs: sliceOrSeed(frame, remaining) })
      remaining = []
      remainderIsFragment = false
      if (!isLast) seams.push(false)

      return
    }

    slices.push({ id: frame.id, paragraphs: sliceOrSeed(frame, split.kept) })
    if (!isLast) seams.push(split.overflowIsFragment)
    remaining = split.overflow
    remainderIsFragment = split.overflowIsFragment
  })

  return { slices, seams, remainder: remaining, remainderIsFragment }
}

/**
 * The subset of `slices` whose frame's content actually changed.
 *
 * Separate from `distributeStory` on purpose. Distribution is head-scoped, so every trigger produces
 * a slice for every member — persisting all of them on a 500 ms debounce would be real write
 * amplification, and `markEdited` acquires element locks. But the callers that split one story into
 * two (link, unlink) need the unfiltered list, so the filter cannot live inside the walk.
 *
 * A frame already holding only a style seed is reported unchanged even though the rebuilt seed is a
 * different object: comparing two independently-constructed seeds structurally would depend on key
 * insertion order across the two construction paths.
 */
export function changedSlices(
  frames: readonly StoryFrame[],
  slices: readonly StorySlice[]
): StorySlice[] {
  const byId = new Map(frames.map((frame) => [frame.id, frame]))

  return slices.filter((slice) => {
    const frame = byId.get(slice.id)

    if (!frame) return true
    if (!paragraphsHaveText(slice.paragraphs) && !paragraphsHaveText(frame.paragraphs)) return false

    return JSON.stringify(slice.paragraphs) !== JSON.stringify(frame.paragraphs)
  })
}
